import { useRef, useState, useCallback, useMemo } from "react";
import { StepContainer, Step } from "newui/components/Stepper";
import {
	EVALUATION_STEP_META,
	EVALUATION_STEP_IDS,
} from "../../constants/evaluation.constants";
import { EvaluationCriteriaStep } from "../steps/EvaluationCriteriaStep";
import { EvaluationPanelStep } from "../steps/EvaluationPanelStep";
import { InitialEvaluationStep } from "../steps/InitialEvaluationStep";
import { ClarificationsStep } from "../steps/ClarificationsStep";
import { RevisionsStep } from "../steps/RevisionsStep";
import { PanelMeetingStep } from "../steps/PanelMeetingStep";
import { FinalEvaluationStep } from "../steps/FinalEvaluationStep";
import type { EvaluationState } from "../../types/evaluation.type";
import type { PanelMode } from "../../types/panel.type";
import { Rfx } from "newui/features/Rfx/types/rfx.types";

interface RFxEvaluationStepsProps {
	rfxData: Rfx;
	rfxSubmissionId?: number;
	evaluationState: EvaluationState | null;
	isRfxLead?: boolean;
	onComplete?: () => void;
	onStepChange?: (step: number) => void;
	onPanelModeChange?: (mode: PanelMode) => void;
	onAdvanceStep?: (step: number) => Promise<void>;
}

const ALL_STEP_IDS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Maps a backend step index (0-based into the full 7-step array) to the
 * corresponding index in the filtered/visible steps array.
 *
 * For example, in solo mode (steps [1,3,4,5,6,7]):
 *   backend step 0 (Criteria, id=1) -> filtered index 0
 *   backend step 2 (Initial, id=3) -> filtered index 1
 *   backend step 3 (Clarifications, id=4) -> filtered index 2
 */
export const mapBackendStepToFilteredIndex = (
	backendStepIndex: number,
	activeStepIds: readonly number[]
): number => {
	// Convert backend 0-based index to 1-based step ID
	const backendStepId = backendStepIndex + 1;

	// Find this step ID in the filtered list
	const filteredIndex = activeStepIds.indexOf(backendStepId);
	if (filteredIndex !== -1) {
		return filteredIndex;
	}

	// If the exact step is not in the filtered list (e.g., Panel step removed),
	// find the next visible step after it
	const nextVisibleIndex = activeStepIds.findIndex(
		(id) => id > backendStepId
	);
	if (nextVisibleIndex !== -1) {
		return nextVisibleIndex;
	}

	// Fallback to last step
	return Math.max(0, activeStepIds.length - 1);
};

/**
 * Determines whether a step at the given filtered index should be enabled,
 * based on the backend's current step progress (also remapped to filtered index).
 */
const getStepEnabled = (
	filteredStepIndex: number,
	mappedCurrentStep: number,
	evaluationState: EvaluationState | null
): boolean => {
	if (filteredStepIndex === 0) {
		return true;
	}

	if (!evaluationState) {
		return false;
	}

	// Enable steps up to one ahead of current progress so the user can advance
	return filteredStepIndex <= mappedCurrentStep + 1;
};

interface StepConfig {
	id: number;
	meta: typeof EVALUATION_STEP_META[number];
	readOnly?: boolean;
	nextLabel?: string;
	onBeforeNext?: () => Promise<boolean> | boolean;
	element: React.ReactNode;
}

export const RFxEvaluationSteps: React.FC<RFxEvaluationStepsProps> = ({
	rfxData,
	rfxSubmissionId,
	evaluationState,
	isRfxLead = false,
	onComplete,
	onStepChange,
	onPanelModeChange,
	onAdvanceStep,
}) => {
	const rfxId = rfxData?.id;
	const savedPanelMode = (evaluationState?.panelMode as PanelMode) ?? undefined;
	const panelMemberId = evaluationState?.currentUserContext?.panelMemberId;
	const panelMemberStatus = evaluationState?.currentUserContext?.panelMemberStatus;
	const criteriaBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const panelBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const initialEvalBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const [initialEvalComplete, setInitialEvalComplete] = useState(false);

	const handleInitialEvalCompletionChange = useCallback((isComplete: boolean) => {
		setInitialEvalComplete(isComplete);
	}, []);

	// Determine which step IDs are active based on workflow config
	const activeStepIds = useMemo(() => {
		return evaluationState?.workflowConfig?.steps ?? [...ALL_STEP_IDS];
	}, [evaluationState?.workflowConfig?.steps]);

	// Remap backend step indices to filtered indices
	const backendCurrentStep = evaluationState?.currentStep ?? 0;
	const mappedCurrentStep = mapBackendStepToFilteredIndex(
		backendCurrentStep,
		activeStepIds
	);
	const highestStep = mappedCurrentStep;
	const initialStep = mappedCurrentStep;

	// Build all step configurations with their 1-based IDs
	const allStepConfigs: StepConfig[] = useMemo(() => [
		{
			id: EVALUATION_STEP_IDS.CRITERIA,
			meta: EVALUATION_STEP_META[0],
			readOnly: !isRfxLead || backendCurrentStep >= 2,
			onBeforeNext: async () => {
				if (criteriaBeforeNextRef.current) {
					return criteriaBeforeNextRef.current();
				}
				return true;
			},
			element: (
				<EvaluationCriteriaStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
					isRfxLead={isRfxLead}
					isLocked={
						(savedPanelMode === "manual_panel" || savedPanelMode === "ai_panel") &&
						backendCurrentStep >= 2
					}
					rfxLeadName={rfxData?.rfxLead?.fullName}
					rfxLeadEmail={rfxData?.rfxLead?.email}
					onBeforeNext={(check) => {
						criteriaBeforeNextRef.current = check;
					}}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.PANEL,
			meta: EVALUATION_STEP_META[1],
			readOnly: !isRfxLead || backendCurrentStep >= 3,
			nextLabel: "Start Evaluation",
			onBeforeNext: async () => {
				if (panelBeforeNextRef.current) {
					const valid = await panelBeforeNextRef.current();
					if (!valid) {
						return false;
					}
				}
				// Ensure backend state is updated before InitialEvaluationStep renders
				// and hits scoring endpoints that require current_step >= 2
				if (onAdvanceStep) {
					await onAdvanceStep(2);
				}
				return true;
			},
			element: (
				<EvaluationPanelStep
					rfxData={rfxData}
					isRfxLead={isRfxLead}
					savedPanelMode={savedPanelMode}
					onPanelModeChange={onPanelModeChange}
					onBeforeNext={(check) => {
						panelBeforeNextRef.current = check;
					}}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.INITIAL,
			meta: EVALUATION_STEP_META[2],
			readOnly: !isRfxLead || backendCurrentStep >= 3,
			nextLabel: "Proceed to Clarification Review",
			onBeforeNext: () => {
				if (initialEvalBeforeNextRef.current) {
					return initialEvalBeforeNextRef.current();
				}
				return true;
			},
			element: (
				<InitialEvaluationStep
					rfxData={rfxData}
					evalId={evaluationState?.id}
					currentStep={evaluationState?.currentStep}
					panelMode={savedPanelMode}
					panelMemberId={panelMemberId ?? undefined}
					panelMemberStatus={panelMemberStatus ?? undefined}
					onBeforeNext={(check) => {
						initialEvalBeforeNextRef.current = check;
					}}
					onCompletionChange={handleInitialEvalCompletionChange}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.CLARIFICATIONS,
			meta: EVALUATION_STEP_META[3],
			readOnly: !isRfxLead,
			element: (
				<ClarificationsStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
					isRfxLead={isRfxLead}
					panelMode={savedPanelMode}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.REVISIONS,
			meta: EVALUATION_STEP_META[4],
			element: (
				<RevisionsStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.PANEL_MEETING,
			meta: EVALUATION_STEP_META[5],
			element: (
				<PanelMeetingStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.FINAL,
			meta: EVALUATION_STEP_META[6],
			element: (
				<FinalEvaluationStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
				/>
			),
		},
	], [
		rfxId,
		rfxSubmissionId,
		rfxData,
		evaluationState?.id,
		evaluationState?.currentStep,
		isRfxLead,
		savedPanelMode,
		panelMemberId,
		panelMemberStatus,
		backendCurrentStep,
		onAdvanceStep,
		onPanelModeChange,
		handleInitialEvalCompletionChange,
	]);

	// Filter to only active steps
	const visibleSteps = useMemo(() => {
		return allStepConfigs.filter((step) => activeStepIds.includes(step.id));
	}, [allStepConfigs, activeStepIds]);

	return (
		<StepContainer
			initialStep={initialStep}
			highestStep={highestStep}
			onComplete={onComplete}
			onStepChange={onStepChange}
		>
			{visibleSteps.map((step, filteredIndex) => {
				const isInitialEvalStep = step.id === EVALUATION_STEP_IDS.INITIAL;
				const stepEnabled = getStepEnabled(filteredIndex, mappedCurrentStep, evaluationState);

				return (
					<Step
						key={step.id}
						name={step.meta.name}
						description={step.meta.description}
						enabled={isInitialEvalStep ? stepEnabled && initialEvalComplete : stepEnabled}
						readOnly={step.readOnly}
						nextLabel={step.nextLabel}
						onBeforeNext={step.onBeforeNext}
					>
						{step.element}
					</Step>
				);
			})}
		</StepContainer>
	);
};
